import { Component } from 'react';
import { connect } from 'react-redux';

import { Loader } from '@ce-cfbdigital/cgl-cfb-fe-foundation';
import classNames from 'classnames';
import DOMPurify from 'dompurify';
import PropTypes from 'prop-types';
import PubSub from 'pubsub-js';

import notificationActions from '../actions/notificationActions';
import * as types from '../constants';
import CustomIcon from '../CustomIcons';
import InfiniteScroll from '../InfiniteScroll';
import NotificationItem from '../NotificationItem';
import NotificationPreferences from '../NotificationPreferences';
import { getAppConfig, timeStampBasedSort } from '../utilities';
import { checkForNotificationPermission } from '../utilities/auth';
import { getActionableNotifications, updateSessionStorage } from '../utilities/common';
import * as analyticsConst from '../utilities/constants';

import './styles/index.css';

export class NotificationsDrawer extends Component {
  constructor(props) {
    super(props);
    const {
      labels: { maxNotifications }
    } = props;
    this.state = {
      itemsToLoad: maxNotifications
    };
    const enableNotificationUrl = updateSessionStorage.getItem(types.NOTIFICATION_ENABLE_URL);
    if (enableNotificationUrl && enableNotificationUrl.includes(types.FROM_NOTIFICATIONS)) {
      props.toggleNotificationsDrawer();
      props.toggleNotificationsPreference();
      updateSessionStorage.removeItem(types.NOTIFICATION_ENABLE_URL);
    }

    this.handleCloseClick = this.handleCloseClick.bind(this);
  }

  componentDidUpdate(prevProps) {
    const { showNotificationsDrawer } = this.props;
    // check if showNotificationsDrawer has changed from redux state udpate
    if (!prevProps.showNotificationsDrawer && prevProps.showNotificationsDrawer !== showNotificationsDrawer) {
      const analyticsData = {
        capability: analyticsConst.NOTIFICATIONS_CAPABILITY,
        description: analyticsConst.GLOBAL_NOTIFICATIONS_OPEN_DESCP,
        pageName: analyticsConst.NOTIFICATIONS_PAGE_NAME,
        event: analyticsConst.GLOBAL_NOTIFICATIONS_OPEN_EVENT
      };
      PubSub.publish(analyticsConst.GLOBAL_NOTIFICATIONS_ANALYTICS, analyticsData);
    }
  }

  handleLoadMoreNotifications = () => {
    const {
      labels: { maxNotifications }
    } = this.props;
    const { itemsToLoad } = this.state;
    this.setState({ itemsToLoad: itemsToLoad + maxNotifications });
  };

  handleClearAll = () => {
    const { removeAllNotifications, webNotifications } = this.props;
    if (webNotifications.length) {
      removeAllNotifications(webNotifications, true);
      const analyticsData = {
        capability: analyticsConst.NOTIFICATIONS_CAPABILITY,
        description: analyticsConst.GLOBAL_NOTIFICATIONS_CLEAR_ALL_DESCP,
        pageName: analyticsConst.NOTIFICATIONS_PAGE_NAME,
        element: { name: analyticsConst.GLOBAL_NOTIFICATIONS_CLEAR_ALL_ELEMENT },
        event: analyticsConst.NOTIFICATIONS_CLICK_ACTION_EVENT
      };
      PubSub.publish(analyticsConst.GLOBAL_NOTIFICATIONS_ANALYTICS, analyticsData);
    }
  };

  handleReadNotification = (e, notifApiData, relativePath) => {
    const { path, notificationRead, toggleNotificationsDrawer, webNotifications, notificationReadException } =
      this.props;
    const { notificationId: id, read: isRead, messageLabel: notificationType } = notifApiData;
    e.preventDefault();
    const analyticsData = {
      capability: analyticsConst.NOTIFICATIONS_CAPABILITY,
      description: analyticsConst.GLOBAL_NOTIFICATIONS_READ_DESCP,
      pageName: analyticsConst.NOTIFICATIONS_PAGE_NAME,
      event: analyticsConst.GLOBAL_NOTIFICATIONS_READ_EVENT,
      notification: { type: notificationType }
    };
    PubSub.publish(analyticsConst.GLOBAL_NOTIFICATIONS_ANALYTICS, analyticsData);
    if (id) {
      const urlPathList = path.split('/').filter(item => item);
      notificationRead(id, () => this.handleNavigation(urlPathList, relativePath), isRead);
      if (urlPathList[urlPathList.length - 1] === types.CONTRACTS_WITH_SMALL_C) {
        toggleNotificationsDrawer();
        notificationReadException(id, webNotifications);
      }
    }
  };

  handleCloseClick(e) {
    e.preventDefault();
    if (e.keyCode !== 9) {
      const { toggleNotificationsPreference } = this.props;
      toggleNotificationsPreference();
    }
  }

  filterWebNotifications = allWebNotifications => {
    const { groups } = this.props;
    const filteredBasedOnTitle =
      allWebNotifications &&
      groups.length &&
      allWebNotifications.length &&
      allWebNotifications.filter(
        item => checkForNotificationPermission(item.messageTitle, groups) && item.dismissed === 0
      );
    return timeStampBasedSort(filteredBasedOnTitle);
  };

  getWebNotificationsTileData = notifDetail => {
    const {
      config: {
        notificationList: { tileOptions }
      }
    } = this.props;
    if (tileOptions && tileOptions.length) {
      return tileOptions.find(item => item.apiKey === notifDetail.messageTitle);
    }
    return {};
  };

  getNotificationsItemToRender = (webNotifications, labels) => {
    const { itemsToLoad } = this.state;
    const filteredOutNotifications = this.filterWebNotifications(webNotifications);
    const filteredOutNotificationswithLimit =
      filteredOutNotifications && filteredOutNotifications.length && filteredOutNotifications.slice(0, itemsToLoad);

    return (
      <div className="notifications-list-wrapper">
        {filteredOutNotificationswithLimit && filteredOutNotificationswithLimit.length ? (
          <InfiniteScroll
            handleLoadMore={this.handleLoadMoreNotifications}
            isLoadedAllResults={itemsToLoad >= filteredOutNotifications.length}
            threshold={0.7}
          >
            {filteredOutNotificationswithLimit.map(item => (
              <NotificationItem
                notificationTileInfo={this.getWebNotificationsTileData(item)}
                apiData={item}
                key={item.notificationId}
                handleClose={this.removeASingleWebNotification}
                handleRead={this.handleReadNotification}
              />
            ))}
          </InfiniteScroll>
        ) : (
          <div className="zero-notifications">
            <p>{labels.noNewNotifications}</p>
          </div>
        )}
      </div>
    );
  };

  removeASingleWebNotification = (e, notifApiData) => {
    const { notificationId: id, messageLabel: notificationType } = notifApiData;
    e.preventDefault();
    if (id) {
      const { removeANotification } = this.props;
      removeANotification(id);
      const analyticsData = {
        capability: analyticsConst.NOTIFICATIONS_CAPABILITY,
        description: analyticsConst.GLOBAL_NOTIFICATIONS_DISMISS_DESCP,
        pageName: analyticsConst.NOTIFICATIONS_PAGE_NAME,
        event: analyticsConst.GLOBAL_NOTIFICATIONS_DISMISS_EVENT,
        notification: { type: notificationType }
      };
      PubSub.publish(analyticsConst.GLOBAL_NOTIFICATIONS_ANALYTICS, analyticsData);
    }
  };

  handleNavigation = (urlList, desiredUrlPath) => {
    const { path } = getAppConfig('server');
    const targetPathSegments = desiredUrlPath?.value?.split('/') || [];
    const targetPage = targetPathSegments[2];

    if (urlList[urlList.length - 1] !== targetPage) {
      const navigationPath = `${path}/${targetPathSegments.slice(2).join('/')}`;
      window.location.href = DOMPurify.sanitize(navigationPath);
    }
  };

  closeNotification(e) {
    const { toggleNotificationsDrawer, toggleNotificationsPreference } = this.props;
    if (e.keyCode !== 9) {
      toggleNotificationsDrawer();
      toggleNotificationsPreference(false);
    }
  }

  render() {
    const {
      showNotificationsDrawer,
      labels,
      userPreferences,
      config,
      loading,
      webNotifications,
      groups,
      isNotificationPreference
    } = this.props;
    const {
      notificationPreferences: { labels: notificationPreferencesLabels }
    } = config;

    return (
      <div className="modal">
        {showNotificationsDrawer && (
          <div
            className="backdrop"
            onClick={e => this.closeNotification(e)}
            role="presentation"
            onKeyDown={e => this.closeNotification(e)}
          />
        )}
        <div className={classNames('notifications-drawer', showNotificationsDrawer && 'open')}>
          <div className="notifications-header-wrapper">
            <div className="notifications-header">
              <div className="notifications-header-status">
                <div className="header-icon">
                  {!isNotificationPreference && <CustomIcon type="notifications" size="24" fill="#658D1B" />}
                  <h2>{!isNotificationPreference ? labels.title : notificationPreferencesLabels.title}</h2>
                </div>
                {!isNotificationPreference && getActionableNotifications(webNotifications, groups).length ? (
                  <div className="clear-all">
                    <button type="button" className="button btn-clr-all" onClick={() => this.handleClearAll()}>
                      {labels.clearAll}
                    </button>
                  </div>
                ) : (
                  ''
                )}
              </div>

              {(checkForNotificationPermission(types.CONTRACTS, groups) ||
                checkForNotificationPermission(types.ORDERS, groups) ||
                checkForNotificationPermission(types.DOCUMENTS, groups)) && (
                <div
                  className="notification-toggle"
                  onClick={this.handleCloseClick}
                  role="button"
                  tabIndex={0}
                  onKeyDown={this.handleCloseClick}
                >
                  {isNotificationPreference ? (
                    <CustomIcon type="backArrow" size="24" fill="#666666" />
                  ) : (
                    <CustomIcon type="settings" size="24" fill="#666666" />
                  )}
                </div>
              )}
            </div>
            {isNotificationPreference ? (
              <NotificationPreferences userPreferences={userPreferences} config={config} groups={groups} />
            ) : (
              this.getNotificationsItemToRender(webNotifications, labels)
            )}
            <Loader isPageLoader={loading} />
          </div>
        </div>
      </div>
    );
  }
}

NotificationsDrawer.propTypes = {
  labels: PropTypes.instanceOf(Object),
  config: PropTypes.instanceOf(Object).isRequired,
  showNotificationsDrawer: PropTypes.bool,
  isNotificationPreference: PropTypes.bool,
  toggleNotificationsDrawer: PropTypes.func.isRequired,
  toggleNotificationsPreference: PropTypes.func.isRequired,
  userPreferences: PropTypes.instanceOf(Object),
  webNotifications: PropTypes.instanceOf(Object),
  loading: PropTypes.bool,
  removeANotification: PropTypes.func.isRequired,
  removeAllNotifications: PropTypes.func.isRequired,
  notificationRead: PropTypes.func.isRequired,
  notificationReadException: PropTypes.func.isRequired,
  path: PropTypes.string.isRequired,
  groups: PropTypes.instanceOf(Object)
};

NotificationsDrawer.defaultProps = {
  labels: {
    title: '',
    backToTop: '',
    noNewNotifications: '',
    clearAll: '',
    maxNotifications: '8'
  },
  showNotificationsDrawer: false,
  isNotificationPreference: false,
  userPreferences: {},
  webNotifications: PropTypes.shape([]),
  loading: false,
  groups: PropTypes.shape([])
};

const mapStateToProps = state => ({
  showNotificationsDrawer: (state.alerts && state.alerts.showNotificationsDrawer) || false,
  isNotificationPreference: state.alerts && state.alerts.isNotificationPreference,
  userPreferences: (state.alerts && state.alerts.userPreferences) || {},
  loading: (state.alerts && state.alerts.loading) || false,
  webNotifications: (state && state.alerts && state.alerts.webNotifications) || {}
});

const mapDispatchToProps = dispatch => ({
  toggleNotificationsDrawer: () => dispatch(notificationActions.toggleNotificationDrawer()),
  toggleNotificationsPreference: (status = true) => dispatch(notificationActions.toggleNotificationsPreference(status)),
  removeANotification: id => dispatch(notificationActions.deleteNotification(id)),
  removeAllNotifications: (filteredNotifications, deleteAll) =>
    dispatch(notificationActions.deleteAllNotifications(filteredNotifications, deleteAll)),
  notificationRead: (id, callBack, isNotificationAlreadyRead) =>
    dispatch(notificationActions.notificationRead(id, callBack, isNotificationAlreadyRead)),
  notificationReadException: (id, allNotifications) =>
    dispatch(notificationActions.notificationReadException(id, allNotifications))
});

export default connect(mapStateToProps, mapDispatchToProps)(NotificationsDrawer);
